import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

interface WorkWeekStepProps {
  selectedDays: number[];
  onChange: (selectedDays: number[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const daysOfWeek = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const WorkWeekStep = ({
  selectedDays,
  onChange,
  onNext,
  onBack,
}: WorkWeekStepProps) => {
  const [error, setError] = useState<string | null>(null);
  const boxBgColor = useColorModeValue('gray.50', 'gray.700');

  const handleChange = (values: string[]) => {
    setError(null);
    // Convert string array to number array
    const numericValues = values.map(val => parseInt(val, 10));
    onChange(numericValues);
  };

  const handleNext = () => {
    if (selectedDays.length === 0) {
      setError('Please select at least one work day');
      return;
    }
    onNext();
  };

  return (
    <Box>
      <Text mb={6}>
        Select the days of the week you typically work. This helps calculate your daily
        billable hour targets more accurately by excluding non-working days.
      </Text>

      <Box
        p={6}
        mb={8}
        bg={boxBgColor}
        borderRadius="md"
        boxShadow="sm"
      >
        <CheckboxGroup
          colorScheme="blue"
          value={selectedDays.map(day => day.toString())}
          onChange={handleChange}
        >
          <Stack spacing={4}>
            {daysOfWeek.map(day => (
              <Checkbox key={day.value} value={day.value.toString()}>
                {day.label}
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      </Box>

      {error && (
        <Text color="red.500" mb={4}>
          {error}
        </Text>
      )}

      <Flex justify="space-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button colorScheme="blue" onClick={handleNext}>
          Next
        </Button>
      </Flex>
    </Box>
  );
};

export default WorkWeekStep;